import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Pencil, CheckCircle2, Send, Ban, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { documentsApi, settingsApi, api, BUY_TYPES, FINANCIAL_TYPES } from "@/api";
import { useLang, fmtMoney, fmtDate, fmtDateTime } from "@/i18n";
import { StatusBadge } from "@/components/Badges";
import { PaymentsSection } from "@/components/PaymentsSection";
import { MyDataPanel } from "@/components/MyDataPanel";

export default function DocumentDetail() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState({});

  useEffect(() => {
    documentsApi.get(id).then((d) => {
      setDoc(d);
      api.get(`/contacts/${d.contact_id}`).then((r) => setContact(r.data)).catch(() => {});
    }).catch(() => toast.error(t("error")));
    settingsApi.get().then(setCompany);
  }, [id, t]);

  const setStatus = async (status) => {
    try { setDoc(await documentsApi.setStatus(id, status)); toast.success(t("status_updated")); }
    catch { toast.error(t("error")); }
  };

  if (!doc) return <div className="text-slate-500 text-sm">…</div>;
  const isPurchase = BUY_TYPES.includes(doc.type);
  const issuer = isPurchase ? contact || {} : company;
  const recipient = isPurchase ? company : contact || {};

  return (
    <div className="space-y-5 page-enter max-w-5xl" data-testid="document-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="document-back-button"><ArrowLeft size={18} /></Button>
          <h1 className="text-2xl font-heading font-extrabold tracking-tight font-mono">{doc.number}</h1>
          {doc.supplier_number && <div className="text-sm text-slate-500 font-mono" data-testid="detail-supplier-number">{t("supplier_number")}: {doc.supplier_number}</div>}
          <StatusBadge doc={doc} />
        </div>
        <div className="flex flex-wrap gap-2">
          {doc.status === "draft" && <Button size="sm" variant="outline" onClick={() => setStatus("issued")} data-testid="status-issue-button"><Send size={14} className="mr-1" />{t("mark_issued")}</Button>}
          {doc.status === "issued" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus("paid")} data-testid="status-paid-button"><CheckCircle2 size={14} className="mr-1" />{t("mark_paid")}</Button>}
          {doc.status !== "cancelled" && doc.status !== "paid" && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setStatus("cancelled")} data-testid="status-cancel-button"><Ban size={14} className="mr-1" />{t("mark_cancelled")}</Button>}
          <Link to={`/documents/${id}/edit`}><Button size="sm" variant="outline" data-testid="document-edit-button"><Pencil size={14} className="mr-1" />{t("edit")}</Button></Link>
          <Button size="sm" className="bg-blue-800 hover:bg-blue-700" onClick={() => window.print()} data-testid="document-print-button"><Printer size={14} className="mr-1" />{t("print_pdf")}</Button>
        </div>
      </div>

      <div id="print-area" className="bg-white text-slate-900 border border-slate-200 shadow-sm p-8 sm:p-12 space-y-8" data-testid="document-print-area">
        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{t("issuer")}</div>
            <div className="font-heading font-extrabold text-xl">{issuer.name}</div>
            <div className="text-sm text-slate-600">{issuer.activity}</div>
            <div className="text-sm text-slate-600 mt-1">{issuer.address}{issuer.city ? `, ${issuer.city}` : ""}</div>
            <div className="text-sm text-slate-600 font-mono">{t("vat_id")}: {issuer.afm} · {t("tax_office")}: {issuer.doy}</div>
            <div className="text-sm text-slate-600">{issuer.phone} · {issuer.email}</div>
          </div>
          <div className="text-right">
            <div className="font-heading font-bold text-2xl uppercase tracking-tight">{t(`doc_${doc.type}`)}</div>
            <div className="font-mono text-lg mt-1">{doc.number}</div>
            <div className="text-sm text-slate-600 mt-3 space-y-0.5">
              <div>{t("issue_date")}: <span className="font-mono">{fmtDate(doc.issue_date, lang)}</span></div>
              {doc.due_date && <div>{t("due_date")}: <span className="font-mono">{fmtDate(doc.due_date, lang)}</span></div>}
              <div>{t("payment_method")}: {t(`pay_${doc.payment_method}`)}</div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{t("recipient")}</div>
          <div className="font-semibold text-lg">{recipient.name || doc.contact_name}</div>
          <div className="text-sm text-slate-600">{recipient.address}{recipient.city ? `, ${recipient.city}` : ""}</div>
          <div className="text-sm text-slate-600 font-mono">{t("vat_id")}: {recipient.afm || "—"} · {t("tax_office")}: {recipient.doy || "—"}</div>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-900 text-left text-xs uppercase tracking-wider">
              <th className="py-2 w-8">#</th>
              <th className="py-2">{t("description")}</th>
              <th className="py-2 text-right w-20">{t("quantity")}</th>
              <th className="py-2 text-right w-28">{t("unit_price")}</th>
              <th className="py-2 text-right w-20">{t("vat_rate")}</th>
              <th className="py-2 text-right w-32">{t("line_total")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {doc.items.map((it, i) => (
              <tr key={i}>
                <td className="py-2.5 text-slate-500 font-mono">{i + 1}</td>
                <td className="py-2.5">{it.description}{it.account_code && <span className="ml-2 font-mono text-xs text-slate-500" data-testid={`detail-line-account-${i}`}>[{it.account_code}]</span>}</td>
                <td className="py-2.5 text-right font-mono">{it.quantity}</td>
                <td className="py-2.5 text-right font-mono">{fmtMoney(it.unit_price)}</td>
                <td className="py-2.5 text-right font-mono">{it.vat_rate}%{it.vat_deductible === false && <span className="ml-1 text-[10px] uppercase text-amber-600" data-testid={`detail-line-nondeductible-${i}`}>{t("non_deductible_short")}</span>}</td>
                <td className="py-2.5 text-right font-mono">{fmtMoney(it.quantity * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between items-end gap-8">
          <div className="text-sm text-slate-600 flex-1">
            {doc.notes && <><div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{t("notes")}</div><p className="whitespace-pre-wrap">{doc.notes}</p></>}
            {company.iban && <div className="mt-4 font-mono text-xs">IBAN: {company.iban}</div>}
          </div>
          <div className="w-72 font-mono text-sm space-y-1.5">
            <div className="flex justify-between"><span className="font-body text-slate-500">{t("subtotal")}</span><span data-testid="detail-subtotal">{fmtMoney(doc.subtotal)}</span></div>
            {Object.entries(doc.vat_breakdown).map(([rate, v]) => (
              <div key={rate} className="flex justify-between text-slate-600"><span className="font-body">{t("vat_amount")} {rate}%</span><span>{fmtMoney(v)}</span></div>
            ))}
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-lg font-semibold">
              <span className="font-body">{t("total_amount")}</span><span data-testid="detail-grand-total">{fmtMoney(doc.total)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 pt-8">
          <div className="border-t border-dashed border-slate-400 pt-2 text-xs uppercase tracking-widest text-slate-500 text-center">{t("issuer")} — {t("signature")}</div>
          <div className="border-t border-dashed border-slate-400 pt-2 text-xs uppercase tracking-widest text-slate-500 text-center">{t("recipient")} — {t("signature")}</div>
        </div>
      </div>

      {FINANCIAL_TYPES.includes(doc.type) && <MyDataPanel doc={doc} onChange={setDoc} />}
      {FINANCIAL_TYPES.includes(doc.type) && <PaymentsSection doc={doc} onChange={setDoc} />}
      <div className="erp-card no-print" data-testid="document-history">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
          <History size={18} className="text-blue-700" /><h3 className="font-heading font-semibold">{t("history")}</h3>
          <span className="ml-auto text-xs text-slate-500 font-mono" data-testid="document-created-by">{t("created_by")}: {doc.created_by || "—"} · {t("updated_by")}: {doc.updated_by || "—"}</span>
        </div>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
          {[...(doc.history || [])].reverse().map((h, i) => (
            <li key={i} className="px-5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`history-row-${i}`}>
              <span className="font-mono text-xs text-slate-500">{fmtDateTime(h.at)}</span>
              <span className="font-semibold">{h.user || "—"}</span>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs">{t(`hist_${h.action}`)}</span>
              {(h.changes || []).map((c, j) => (
                <span key={j} className="text-xs text-slate-600 dark:text-slate-300">
                  {t(c.field === "items" ? "line_items" : c.field)}{c.to !== undefined && <>: <span className="font-mono">{c.field === "status" ? t(`status_${c.from}`) : c.from ?? "—"} → {c.field === "status" ? t(`status_${c.to}`) : c.to}</span></>}
                </span>
              ))}
            </li>
          ))}
          {!(doc.history || []).length && <li className="px-5 py-4 text-slate-500 text-sm">{t("no_history")}</li>}
        </ul>
      </div>
    </div>
  );
}
